import type { CustomProxy, Provider } from '@unipile/types';
import { ChevronsUpDown, GlobeIcon } from 'lucide-react';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocationConfig } from '../../../hooks/useLocationConfig';
import { useShowLocationConfig } from '../../../hooks/useShowLocationConfig';
import { Button } from '../../shadcn/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../shadcn/Dialog';
import { Field, FieldContent, FieldDescription, FieldGroup, FieldLabel } from '../../shadcn/Field';
import { Input } from '../../shadcn/Input';
import { Label } from '../../shadcn/Label';
import { RadioGroup, RadioGroupItem } from '../../shadcn/RadioGroup';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../shadcn/Select';
import { proxyCountryOptions } from './proxy-country-options';

interface LocationConfigProps {
  isLoading?: boolean;
  className?: string;
  provider: Exclude<Provider, 'mock'>;
}

const protocolOptions = [
  { value: 'https', label: 'HTTPS' },
  { value: 'http', label: 'HTTP' },
  { value: 'socks5', label: 'SOCKS5' },
  { value: 'socks4', label: 'SOCKS4' },
];

type LocationMethod = 'current' | 'default' | 'country' | 'custom';

export function LocationConfig({ isLoading = false, className, provider }: LocationConfigProps) {
  const { locationConfig, setLocationConfig } = useLocationConfig();

  const { allowUserProxyOverride, allowUserCountryOverride, providerWithAutoProxy, showLocationConfig } =
    useShowLocationConfig(provider);

  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  // Get current config from context
  const currentConfig = locationConfig[provider] || { custom_proxy: undefined, auto_proxy_config: undefined };

  const [locationMethod, setLocationMethod] = React.useState<LocationMethod>(() => {
    if (providerWithAutoProxy) {
      if (currentConfig.custom_proxy) return 'custom';
      if (currentConfig.auto_proxy_config?.country) return 'country';
      return 'default';
    } else {
      if (currentConfig.custom_proxy) return 'custom';
      return 'current';
    }
  });
  const [selectedCountry, setSelectedCountry] = React.useState<string>(currentConfig.auto_proxy_config?.country || '');
  const [proxyData, setProxyData] = React.useState<CustomProxy | undefined>(currentConfig.custom_proxy);

  const proxyForm = useForm<CustomProxy>({
    defaultValues: {
      host: '',
      port: 8080,
      username: '',
      password: '',
      protocol: 'https',
    },
  });

  // Get available methods based on provider and feature flags
  const getAvailableMethods = (): LocationMethod[] => {
    if (providerWithAutoProxy) {
      const methods: LocationMethod[] = ['default'];
      if (allowUserCountryOverride) methods.push('country');
      if (allowUserProxyOverride) methods.push('custom');
      return methods;
    } else {
      const methods: LocationMethod[] = ['current'];
      if (allowUserProxyOverride) methods.push('custom');
      return methods;
    }
  };

  // Get summary text for the field
  const getSummaryText = (): string => {
    if (providerWithAutoProxy) {
      if (locationMethod === 'default') return t('LocationConfig.summary.default');
      if (locationMethod === 'country') {
        const country = proxyCountryOptions.find((c) => c.value === selectedCountry);
        return country ? t('LocationConfig.summary.proxyIn', { country: country.label }) : t('LocationConfig.summary.country');
      }
      if (locationMethod === 'custom') return t('LocationConfig.summary.custom');
      return t('LocationConfig.summary.default');
    } else {
      if (locationMethod === 'current') return t('LocationConfig.summary.current');
      if (locationMethod === 'custom') return t('LocationConfig.summary.custom');
      return t('LocationConfig.summary.current');
    }
  };

  const handleLocationMethodChange = (method: LocationMethod) => {
    setLocationMethod(method);

    // Reset related data when changing method, but don't apply changes yet
    if (method === 'current' || method === 'default') {
      setSelectedCountry('');
      setProxyData(undefined);
      // Don't call onProxyChange/onCountryChange here - wait for apply
    } else if (method === 'country') {
      setProxyData(undefined);
      // Don't call onProxyChange here - wait for apply
    } else if (method === 'custom') {
      setSelectedCountry('');
      // Don't call onCountryChange here - wait for apply
    }
  };

  const handleCountryChange = (country: string) => {
    setSelectedCountry(country);
    // Don't call onCountryChange here - wait for apply
  };

  const handleApply = async () => {
    if (locationMethod === 'current') {
      setLocationConfig(provider, { custom_proxy: undefined, auto_proxy_config: undefined });
    } else if (locationMethod === 'default') {
      setLocationConfig(provider, { custom_proxy: undefined, auto_proxy_config: undefined });
    } else if (locationMethod === 'country') {
      setLocationConfig(provider, { custom_proxy: undefined, auto_proxy_config: { country: selectedCountry } });
    } else if (locationMethod === 'custom') {
      const isValid = await proxyForm.trigger();
      if (isValid) {
        const proxyData = proxyForm.getValues();
        setProxyData(proxyData);
        setLocationConfig(provider, { custom_proxy: proxyData, auto_proxy_config: undefined });
      } else {
        return; // Don't close modal if validation fails
      }
    }

    setIsModalOpen(false);
  };

  const handleModalOpen = () => {
    // Preselect the currently active choice when reopening
    if (providerWithAutoProxy) {
      if (proxyData) setLocationMethod('custom');
      else if (selectedCountry) setLocationMethod('country');
      else setLocationMethod('default');
    } else {
      if (proxyData) setLocationMethod('custom');
      else setLocationMethod('current');
    }
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    // Reset form and selections to their current applied state when closing without applying
    if (providerWithAutoProxy) {
      if (proxyData) setLocationMethod('custom');
      else if (selectedCountry) setLocationMethod('country');
      else setLocationMethod('default');
    } else {
      if (proxyData) setLocationMethod('custom');
      else setLocationMethod('current');
    }
    setIsModalOpen(false);
  };

  const availableMethods = getAvailableMethods();

  if (!showLocationConfig) {
    return null;
  }

  return (
    <div className={className}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <GlobeIcon className="size-4" />
          <Label className="text-foreground text-sm font-medium">{t('LocationConfig.field.label')}</Label>
        </div>
        <Button variant="ghost" size="sm" className="text-muted-foreground pr-0" onClick={handleModalOpen}>
          {getSummaryText()}
          <ChevronsUpDown className="text-muted-foreground ml-2 size-4" />
        </Button>
      </div>

      {/* Location Configuration Modal */}
      <Dialog open={isModalOpen} onOpenChange={(open) => !open && handleModalClose()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('LocationConfig.modal.title')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Location Method Selection */}
            <RadioGroup value={locationMethod} onValueChange={(value) => handleLocationMethodChange(value as LocationMethod)}>
              {availableMethods.map((method) => (
                <div key={method} className="space-y-2">
                  <Field orientation="horizontal">
                    <RadioGroupItem value={method} id={method} />
                    <FieldContent>
                      <FieldLabel htmlFor={method}>
                        {method === 'current' && t('LocationConfig.methods.current.label')}
                        {method === 'default' && t('LocationConfig.methods.default.label')}
                        {method === 'country' && t('LocationConfig.methods.country.label')}
                        {method === 'custom' && t('LocationConfig.methods.custom.label')}
                      </FieldLabel>
                      <FieldDescription>
                        {method === 'current' && t('LocationConfig.methods.current.description')}
                        {method === 'default' && t('LocationConfig.methods.default.description')}
                        {method === 'country' && t('LocationConfig.methods.country.description')}
                        {method === 'custom' && t('LocationConfig.methods.custom.description')}
                      </FieldDescription>
                    </FieldContent>
                  </Field>

                  {/* Conditional sub-forms */}
                  {method === 'country' && locationMethod === 'country' && (
                    <div className="ml-6 space-y-2">
                      <Select value={selectedCountry} onValueChange={handleCountryChange}>
                        <SelectTrigger>
                          <SelectValue placeholder={t('LocationConfig.country.placeholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          {proxyCountryOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {method === 'custom' && locationMethod === 'custom' && (
                    <div className="ml-6 space-y-3 border-l-2 pl-4">
                      <FieldGroup>
                        {/* Host - Full width on first line */}
                        <Controller
                          control={proxyForm.control}
                          name="host"
                          rules={{ required: t('LocationConfig.proxy.host.required') }}
                          render={({ field }) => (
                            <Field>
                              <FieldLabel className="text-xs">{t('LocationConfig.proxy.host.label')}</FieldLabel>
                              <Input placeholder={t('LocationConfig.proxy.host.placeholder')} {...field} />
                            </Field>
                          )}
                        />

                        {/* Port and Protocol - Second line, side by side */}
                        <div className="grid grid-cols-2 gap-3">
                          <Controller
                            control={proxyForm.control}
                            name="port"
                            rules={{
                              required: t('LocationConfig.proxy.port.required'),
                              min: { value: 1, message: t('LocationConfig.proxy.port.min') },
                              max: { value: 65535, message: t('LocationConfig.proxy.port.max') },
                            }}
                            render={({ field }) => (
                              <Field>
                                <FieldLabel className="text-xs">{t('LocationConfig.proxy.port.label')}</FieldLabel>
                                <Input
                                  type="number"
                                  placeholder={t('LocationConfig.proxy.port.placeholder')}
                                  {...field}
                                  onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                                />
                              </Field>
                            )}
                          />

                          <Controller
                            control={proxyForm.control}
                            name="protocol"
                            rules={{ required: t('LocationConfig.proxy.protocol.required') }}
                            render={({ field }) => (
                              <Field>
                                <FieldLabel className="text-xs">{t('LocationConfig.proxy.protocol.label')}</FieldLabel>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                  <SelectTrigger>
                                    <SelectValue placeholder={t('LocationConfig.proxy.protocol.placeholder')} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {protocolOptions.map((option) => (
                                      <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </Field>
                            )}
                          />
                        </div>

                        {/* Username and Password - Third line, side by side */}
                        <div className="grid grid-cols-2 gap-3">
                          <Controller
                            control={proxyForm.control}
                            name="username"
                            render={({ field }) => (
                              <Field>
                                <FieldLabel className="text-xs">{t('LocationConfig.proxy.username.label')}</FieldLabel>
                                <Input
                                  placeholder={t('LocationConfig.proxy.username.placeholder')}
                                  {...field}
                                  value={field.value || ''}
                                />
                              </Field>
                            )}
                          />

                          <Controller
                            control={proxyForm.control}
                            name="password"
                            render={({ field }) => (
                              <Field>
                                <FieldLabel className="text-xs">{t('LocationConfig.proxy.password.label')}</FieldLabel>
                                <Input
                                  type="password"
                                  placeholder={t('LocationConfig.proxy.password.placeholder')}
                                  {...field}
                                  value={field.value || ''}
                                />
                              </Field>
                            )}
                          />
                        </div>
                      </FieldGroup>
                    </div>
                  )}
                </div>
              ))}
            </RadioGroup>

            {/* Modal Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={handleModalClose} disabled={isLoading}>
                {t('LocationConfig.modal.cancel')}
              </Button>
              <Button onClick={handleApply} disabled={isLoading || (locationMethod === 'custom' && !proxyForm.formState.isValid)}>
                {t('LocationConfig.modal.apply')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
